import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import FilterDrawer from '../components/FilterDrawer';
import MachineBottomNav from '../components/MachineBottomNav';
import '../components/MachineBottomNav.css';
import {
  ActionBtn,
  TicketRow,
  ImpactBadge,
  TierBadge,
  IMPACT_CONFIG,
  IMPACT_ORDER,
  DEFAULT_TRIAGE_TIER,
  isNeedsTriage
} from '../components/TicketUi';
import '../components/TicketUi.css';
import './MachineIssues.css';
import { DSIcon, DSSelect, DSTextarea } from '../design-system/primitives';

const TIER_OPTIONS = [0, 2, 3, 4];

// Machine-level urgency, most attention-needed first. A machine can have
// tickets spanning several of these at once - it's bucketed under the single
// worst one, same priority order used everywhere else in this feature
// (server sort order, machine status auto-recompute, the wireframe's queue).
// Bucketed by each ticket's current impact, not by triage status - an
// untriaged ticket already has an impact (set at report time, see
// mapIntakeAnswersToImpact) and belongs in that section; "needs triage" is
// surfaced as a per-ticket badge (TicketRow/NeedsTriageBadge) instead.
const MACHINE_CATEGORY_ORDER = ['game_down', 'tournament_risk', 'observation', 'none'];
const MACHINE_CATEGORY_META = {
  game_down: { label: 'Game Down', color: IMPACT_CONFIG.game_down.dot },
  tournament_risk: { label: 'Tournament Risk', color: IMPACT_CONFIG.tournament_risk.dot },
  observation: { label: 'Observation', color: IMPACT_CONFIG.observation.dot },
  none: { label: 'No Open Issues', color: '#4a5070' }
};

function categorizeMachineGroup(issues) {
  const open = issues.filter((issue) => issue.status === 'open');
  if (open.some((issue) => issue.impact === 'game_down')) return 'game_down';
  if (open.some((issue) => issue.impact === 'tournament_risk')) return 'tournament_risk';
  if (open.some((issue) => issue.impact === 'observation')) return 'observation';
  return 'none';
}

function MachineIssues() {
  const currentUser = authService.getCurrentUser();
  const isAdmin = currentUser?.role === 'admin';
  const techTier = currentUser?.techTier ?? currentUser?.tech_tier ?? 0;
  // Triage is Tier 3+/Admin, on any ticket regardless of prior triage state
  // (docs/MACHINE_TICKET_TIERING_REQUIREMENTS.md §8). Also gates the
  // assignable-users lookup (Tier 3+ server-side) feeding the Issues tab's
  // "Assigned To" filter.
  const canTriage = isAdmin || techTier >= 3;
  const location = useLocation();
  const view = location.pathname.startsWith('/machines/triage')
    ? 'needs_triage'
    : 'by_machine';

  // "By Machine" tab state (filterable, category-sectioned browse view)
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [groupByMachine, setGroupByMachine] = useState(true);
  const defaultFilters = {
    status: 'open',
    impact: '',
    needsTriage: false,
    assignedTo: ''
  };
  const [filterStatus, setFilterStatus] = useState(defaultFilters.status);
  const [filterImpact, setFilterImpact] = useState(defaultFilters.impact);
  const [filterNeedsTriage, setFilterNeedsTriage] = useState(defaultFilters.needsTriage);
  const [filterAssignedTo, setFilterAssignedTo] = useState(defaultFilters.assignedTo);
  const [draftStatus, setDraftStatus] = useState(defaultFilters.status);
  const [draftImpact, setDraftImpact] = useState(defaultFilters.impact);
  const [draftNeedsTriage, setDraftNeedsTriage] = useState(defaultFilters.needsTriage);
  const [draftAssignedTo, setDraftAssignedTo] = useState(defaultFilters.assignedTo);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [expandedMachineIds, setExpandedMachineIds] = useState(new Set());

  // "Needs Triage" tab state / banner - shared dataset of all open tickets
  const [allOpenIssues, setAllOpenIssues] = useState([]);
  const [allOpenLoading, setAllOpenLoading] = useState(true);
  const [allOpenError, setAllOpenError] = useState('');

  const [assignees, setAssignees] = useState([]);

  // Inline triage form (Needs Triage tab)
  const [triagingIssueId, setTriagingIssueId] = useState(null);
  const [triageForm, setTriageForm] = useState({ impact: 'observation', requiredTechTier: DEFAULT_TRIAGE_TIER, note: '' });
  const [triageError, setTriageError] = useState('');
  const [triageSubmitting, setTriageSubmitting] = useState(false);
  const [quickAcceptingId, setQuickAcceptingId] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    loadByMachine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterImpact, filterNeedsTriage, filterAssignedTo]);

  useEffect(() => {
    loadAssignableUsers();
    loadAllOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadByMachine = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterImpact) params.impact = filterImpact;
      if (filterNeedsTriage) params.needsTriage = 'true';
      if (filterAssignedTo) params.assignedTo = filterAssignedTo;

      const queryString = new URLSearchParams(params).toString();
      const issuesRes = await apiService.get(`/machines/issues/all${queryString ? '?' + queryString : ''}`);

      setIssues(issuesRes.data);
    } catch (err) {
      setError('Failed to load issues');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadAllOpen = async () => {
    if (!canTriage) {
      setAllOpenLoading(false);
      return;
    }
    try {
      setAllOpenLoading(true);
      const res = await apiService.get('/machines/issues/all?status=open');
      setAllOpenIssues(res.data);
    } catch (err) {
      setAllOpenError('Failed to load open tickets');
      console.error(err);
    } finally {
      setAllOpenLoading(false);
    }
  };

  const refreshAll = () => {
    loadByMachine();
    loadAllOpen();
  };

  const loadAssignableUsers = async () => {
    // Browsing "By Machine" is universal (Tier 0+), but this list only feeds
    // the Assigned To filter and the endpoint itself is Tier 3+ - skip it
    // below that floor so a lower-tier viewer doesn't get a spurious
    // "no permission" toast just for opening the Issues tab.
    if (!canTriage) return;
    try {
      const response = await apiService.getMachineIssueAssignableUsers();
      setAssignees(response.data || []);
    } catch (err) {
      console.error('Failed to load assignable users:', err);
    }
  };

  const openIssueDetails = (issue) => {
    navigate(`/machine-issues/${issue.id}`);
  };

  const toggleMachineGroup = (machineKey) => {
    setExpandedMachineIds((prev) => {
      const next = new Set(prev);
      if (next.has(machineKey)) {
        next.delete(machineKey);
      } else {
        next.add(machineKey);
      }
      return next;
    });
  };

  const getStatusLabel = (status) => {
    const statusMap = {
      open: 'Open',
      resolved: 'Resolved'
    };
    return statusMap[status] || 'All Statuses';
  };

  const getImpactLabel = (impact) => {
    const impactMap = {
      game_down: 'Game Down',
      tournament_risk: 'Tournament Risk',
      observation: 'Observation'
    };
    return impactMap[impact] || 'All Impacts';
  };

  const openFilters = () => {
    setDraftStatus(filterStatus);
    setDraftImpact(filterImpact);
    setDraftNeedsTriage(filterNeedsTriage);
    setDraftAssignedTo(filterAssignedTo);
    setIsFilterOpen(true);
  };

  const closeFilters = () => {
    setIsFilterOpen(false);
  };

  const applyFilters = () => {
    setFilterStatus(draftStatus);
    setFilterImpact(draftImpact);
    setFilterNeedsTriage(draftNeedsTriage);
    setFilterAssignedTo(draftAssignedTo);
    setIsFilterOpen(false);
  };

  const resetFilters = () => {
    setDraftStatus(defaultFilters.status);
    setDraftImpact(defaultFilters.impact);
    setDraftNeedsTriage(defaultFilters.needsTriage);
    setDraftAssignedTo(defaultFilters.assignedTo);
  };

  const hasFilterChanges =
    draftStatus !== filterStatus ||
    draftImpact !== filterImpact ||
    draftNeedsTriage !== filterNeedsTriage ||
    draftAssignedTo !== filterAssignedTo;
  const hasResetChanges =
    draftStatus !== defaultFilters.status ||
    draftImpact !== defaultFilters.impact ||
    draftNeedsTriage !== defaultFilters.needsTriage ||
    draftAssignedTo !== defaultFilters.assignedTo;

  const formatAssigneeName = (user) => {
    const fullName = `${user?.first_name || user?.firstName || ''} ${user?.last_name || user?.lastName || ''}`.trim();
    return fullName || user?.username || user?.email || 'Unknown User';
  };

  const currentUserOptionLabel = useMemo(() => {
    if (!currentUser?.id) {
      return null;
    }

    const currentUserName = `${currentUser.firstName || currentUser.first_name || ''} ${currentUser.lastName || currentUser.last_name || ''}`.trim();
    return currentUserName ? `My Issues (${currentUserName})` : 'My Issues';
  }, [currentUser]);

  const selectedAssigneeName = filterAssignedTo
    ? (String(filterAssignedTo).toLowerCase() === 'unassigned'
      ? 'Unassigned'
      : String(filterAssignedTo) === String(currentUser?.id)
      ? (currentUserOptionLabel || 'My Issues')
      : formatAssigneeName(assignees.find((user) => String(user.id) === String(filterAssignedTo))))
    : 'All Assignees';

  const filterSummary = `Status: ${getStatusLabel(filterStatus)}${
    filterImpact ? ` | Impact: ${getImpactLabel(filterImpact)}` : ''
  }${filterNeedsTriage ? ' | Needs Triage' : ''} | Assigned To: ${
    selectedAssigneeName || 'All Assignees'
  }`;

  const groupedIssues = useMemo(() => {
    const grouped = issues.reduce((acc, issue) => {
      const machineKey = String(issue.machine_id ?? issue.machineId ?? issue.machine_name);
      if (!acc[machineKey]) {
        acc[machineKey] = {
          machineKey,
          machineName: issue.machine_name || 'Unknown Machine',
          issues: []
        };
      }
      acc[machineKey].issues.push(issue);
      return acc;
    }, {});

    return Object.values(grouped)
      .map((group) => ({
        ...group,
        category: categorizeMachineGroup(group.issues),
        issues: [...group.issues].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        )
      }))
      .sort((a, b) => a.machineName.localeCompare(b.machineName));
  }, [issues]);

  const groupedByCategory = useMemo(() => {
    return MACHINE_CATEGORY_ORDER
      .map((category) => ({
        category,
        groups: groupedIssues.filter((group) => group.category === category)
      }))
      .filter((section) => section.groups.length > 0);
  }, [groupedIssues]);

  // Ungrouped view (groupByMachine off): same category sections, but each
  // issue is its own row (machine name shown per-row) instead of nested
  // under a collapsible machine group.
  const flatByCategory = useMemo(() => {
    return IMPACT_ORDER
      .map((category) => ({
        category,
        issues: issues
          .filter((issue) => issue.impact === category)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      }))
      .filter((section) => section.issues.length > 0);
  }, [issues]);

  useEffect(() => {
    setExpandedMachineIds(new Set());
  }, [groupedIssues]);

  // allOpenIssues arrives pre-sorted (status, impact, created_at ASC) from the server.
  const needsTriageIssues = useMemo(
    () => allOpenIssues.filter((issue) => isNeedsTriage(issue)),
    [allOpenIssues]
  );

  const openTriage = (issue) => {
    setTriagingIssueId(issue.id);
    setTriageForm({
      impact: issue.impact || 'observation',
      requiredTechTier: issue.required_tech_tier ?? DEFAULT_TRIAGE_TIER,
      note: ''
    });
    setTriageError('');
  };

  const cancelTriage = () => {
    setTriagingIssueId(null);
    setTriageError('');
  };

  const quickAccept = async (issue) => {
    try {
      setQuickAcceptingId(issue.id);
      setError('');
      await apiService.triageIssue(issue.id, {
        impact: issue.impact,
        requiredTechTier: DEFAULT_TRIAGE_TIER
      });
      refreshAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept issue');
    } finally {
      setQuickAcceptingId(null);
    }
  };

  const submitTriage = async (issueId) => {
    if (triageForm.requiredTechTier === '') {
      setTriageError('Select a Required Tech Tier.');
      return;
    }
    try {
      setTriageSubmitting(true);
      setTriageError('');
      await apiService.triageIssue(issueId, {
        impact: triageForm.impact,
        requiredTechTier: Number(triageForm.requiredTechTier),
        note: triageForm.note.trim() || undefined
      });
      cancelTriage();
      refreshAll();
    } catch (err) {
      setTriageError(err.response?.data?.error || 'Failed to triage issue');
    } finally {
      setTriageSubmitting(false);
    }
  };

  const renderNeedsTriageBanner = () => {
    if (view === 'needs_triage' || !canTriage || needsTriageIssues.length === 0) return null;
    return (
      <button type="button" className="mi-triage-banner" onClick={() => navigate('/machines/triage')}>
        <span className="mi-triage-banner__icon"><DSIcon name="warning" /></span>
        <span>
          {needsTriageIssues.length} ticket{needsTriageIssues.length !== 1 ? 's' : ''} need{needsTriageIssues.length === 1 ? 's' : ''} triage
        </span>
        <span className="mi-triage-banner__cta">Go to Needs Triage <DSIcon name="arrowRight" /></span>
      </button>
    );
  };

  const renderByMachineTab = () => {
    if (loading) return <div className="loading">Loading issues...</div>;

    return (
      <>
        <div className="page-header page-header--filters">
          <button
            type="button"
            className="filter-trigger"
            onClick={openFilters}
            aria-expanded={isFilterOpen}
          >
            <span className="filter-trigger__text">
              <span className="filter-trigger__label">Filters</span>
              <span className="filter-trigger__summary">{filterSummary}</span>
            </span>
            <span className="filter-trigger__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                <path
                  d="M4 6h16M7 12h10M10 18h4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </button>
        </div>

        <label className="mi-group-toggle" htmlFor="group-by-machine">
          <input
            id="group-by-machine"
            type="checkbox"
            checked={groupByMachine}
            onChange={(e) => setGroupByMachine(e.target.checked)}
          />
          {' '}Group by Machine
        </label>

        <FilterDrawer
          title="Filters"
          summary={filterSummary}
          isOpen={isFilterOpen}
          onOpen={openFilters}
          onClose={closeFilters}
          onApply={applyFilters}
          onReset={resetFilters}
          applyDisabled={!hasFilterChanges}
          resetDisabled={!hasResetChanges}
          showBar={false}
        >
          <div className="filter-drawer__group">
            <label htmlFor="filter-status">Status</label>
            <DSSelect
              id="filter-status"
              value={draftStatus}
              onChange={(e) => setDraftStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
            </DSSelect>
          </div>

          <div className="filter-drawer__group">
            <label htmlFor="filter-impact">Impact</label>
            <DSSelect
              id="filter-impact"
              value={draftImpact}
              onChange={(e) => setDraftImpact(e.target.value)}
            >
              <option value="">All Impacts</option>
              <option value="game_down">Game Down</option>
              <option value="tournament_risk">Tournament Risk</option>
              <option value="observation">Observation</option>
            </DSSelect>
          </div>

          <div className="filter-drawer__group">
            <label htmlFor="filter-needs-triage">
              <input
                id="filter-needs-triage"
                type="checkbox"
                checked={draftNeedsTriage}
                onChange={(e) => setDraftNeedsTriage(e.target.checked)}
              />
              {' '}Needs Triage only
            </label>
          </div>

          <div className="filter-drawer__group">
            <label htmlFor="filter-assigned-to">Assigned To</label>
            <DSSelect
              id="filter-assigned-to"
              value={draftAssignedTo}
              onChange={(e) => setDraftAssignedTo(e.target.value)}
            >
              <option value="">All Assignees</option>
              {currentUser?.id && (
                <option value={String(currentUser.id)}>{currentUserOptionLabel || 'My Issues'}</option>
              )}
              <option value="unassigned">Unassigned</option>
              {assignees
                .filter((user) => String(user.id) !== String(currentUser?.id))
                .map((user) => (
                  <option key={user.id} value={String(user.id)}>
                    {formatAssigneeName(user)}
                  </option>
                ))}
            </DSSelect>
          </div>
        </FilterDrawer>

        <div className="machine-issues__list">
          {groupByMachine ? (
            groupedByCategory.map(({ category, groups }) => (
              <div key={category} className="machine-issues__category">
                <h2 className="mi-section-heading" style={{ color: MACHINE_CATEGORY_META[category].color }}>
                  <DSIcon name="dot" /> {MACHINE_CATEGORY_META[category].label}
                </h2>
                <div className="machine-issues__category-groups">
                  {groups.map((group) => {
                    const isGroupExpanded = expandedMachineIds.has(group.machineKey);
                    const groupId = `machine-group-${group.machineKey}`;

                    return (
                      <section key={group.machineKey} className="machine-issues__group">
                        <button
                          type="button"
                          className="machine-group__header"
                          onClick={() => toggleMachineGroup(group.machineKey)}
                          aria-expanded={isGroupExpanded}
                          aria-controls={groupId}
                        >
                          <span className="machine-group__heading">
                            <span className="machine-group__title">{group.machineName}</span>
                            <span className="machine-group__count">{group.issues.length}</span>
                          </span>
                          <span
                            className={`machine-group__chevron ${isGroupExpanded ? 'is-open' : ''}`}
                            aria-hidden="true"
                          >
                            <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                              <path
                                d="M6 9l6 6 6-6"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </button>

                        {isGroupExpanded && (
                          <div id={groupId} className="machine-group__issues">
                            {group.issues.map((issue) => (
                              <TicketRow
                                key={issue.id}
                                issue={issue}
                                onPress={openIssueDetails}
                                viewerTier={techTier}
                                viewerIsAdmin={isAdmin}
                                showMachineName={false}
                              />
                            ))}
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            flatByCategory.map(({ category, issues: categoryIssues }) => (
              <div key={category} className="machine-issues__category">
                <h2 className="mi-section-heading" style={{ color: MACHINE_CATEGORY_META[category].color }}>
                  <DSIcon name="dot" /> {MACHINE_CATEGORY_META[category].label}
                </h2>
                <div className="machine-issues__flat-rows">
                  {categoryIssues.map((issue) => (
                    <TicketRow
                      key={issue.id}
                      issue={issue}
                      onPress={openIssueDetails}
                      viewerTier={techTier}
                      viewerIsAdmin={isAdmin}
                      showMachineName
                    />
                  ))}
                </div>
              </div>
            ))
          )}

          {issues.length === 0 && (
            <div className="empty-state">
              <p>No issues found matching your filters.</p>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderNeedsTriageTab = () => {
    if (allOpenLoading) return <div className="loading">Loading...</div>;
    if (allOpenError) return <div className="error-message">{allOpenError}</div>;

    if (!canTriage) {
      return (
        <div className="mi-empty">
          <span className="mi-empty__icon"><DSIcon name="lock" /></span>
          <p className="mi-empty__title">Triage is Tier 3+/Admin only</p>
          <p className="mi-empty__body">You don't have permission to triage tickets.</p>
        </div>
      );
    }

    if (needsTriageIssues.length === 0) {
      return (
        <div className="mi-empty">
          <span className="mi-empty__icon"><DSIcon name="check" /></span>
          <p className="mi-empty__title">Nothing to triage</p>
          <p className="mi-empty__body">Every open ticket has an Impact and Required Tier set.</p>
        </div>
      );
    }

    return (
      <div className="mi-queue">
        <div className="mi-queue__rows">
          {needsTriageIssues.map((issue) => {
            const isTriaging = triagingIssueId === issue.id;
            const isQuickAccepting = quickAcceptingId === issue.id;
            return (
              <div key={issue.id} className="mi-triage-card">
                <button type="button" className="mi-triage-card__body" onClick={() => openIssueDetails(issue)}>
                  <p className="mi-triage-card__machine">{issue.machine_name}</p>
                  <div className="mi-triage-card__badges">
                    <ImpactBadge impact={issue.impact} />
                    <TierBadge tier={DEFAULT_TRIAGE_TIER} assumed />
                  </div>
                  <p className="mi-triage-card__desc">{issue.description}</p>
                  <span className="mi-triage-card__meta">Reported by {issue.reporter_name || 'Anonymous'}</span>
                </button>

                {!isTriaging && (
                  <div className="mi-triage-card__actions">
                    <ActionBtn
                      label={isQuickAccepting ? 'Accepting…' : `Accept as ${IMPACT_CONFIG[issue.impact]?.label || 'Observation'} · T${DEFAULT_TRIAGE_TIER}`}
                      variant="primary"
                      onClick={() => quickAccept(issue)}
                      disabled={isQuickAccepting}
                    />
                    <ActionBtn label="Adjust & Triage" variant="ghost" onClick={() => openTriage(issue)} disabled={isQuickAccepting} />
                  </div>
                )}

                {isTriaging && (
                  <div className="mi-triage-form">
                    <p className="mi-triage-form__label">Impact</p>
                    <div className="mi-triage-form__impact-options">
                      {IMPACT_ORDER.map((impact) => (
                        <button
                          key={impact}
                          type="button"
                          className={`mi-triage-form__impact-option ${triageForm.impact === impact ? 'mi-triage-form__impact-option--active' : ''}`}
                          style={
                            triageForm.impact === impact
                              ? { background: IMPACT_CONFIG[impact].bg, borderColor: IMPACT_CONFIG[impact].border, color: IMPACT_CONFIG[impact].text }
                              : undefined
                          }
                          onClick={() => setTriageForm((f) => ({ ...f, impact }))}
                        >
                          <span className="mi-triage-form__impact-dot" style={{ background: triageForm.impact === impact ? IMPACT_CONFIG[impact].dot: 'var(--ds-color-textDisabled)' }} />
                          {IMPACT_CONFIG[impact].label}
                          {triageForm.impact === impact && (
                            <span className="mi-triage-form__impact-check" style={{ color: IMPACT_CONFIG[impact].text }}><DSIcon name="check" /></span>
                          )}
                        </button>
                      ))}
                    </div>

                    <p className="mi-triage-form__label">Required Tech Tier</p>
                    <div className="mi-triage-form__tier-options">
                      {TIER_OPTIONS.map((tier) => (
                        <button
                          key={tier}
                          type="button"
                          className={`mi-triage-form__tier-option ${String(triageForm.requiredTechTier) === String(tier) ? 'mi-triage-form__tier-option--active' : ''}`}
                          onClick={() => setTriageForm((f) => ({ ...f, requiredTechTier: tier }))}
                        >
                          T{tier}
                        </button>
                      ))}
                    </div>

                    <p className="mi-triage-form__label">Note (optional)</p>
                    <DSTextarea
                      className="mi-triage-form__note"
                      value={triageForm.note}
                      onChange={(e) => setTriageForm((f) => ({ ...f, note: e.target.value }))}
                      placeholder="Add context for this triage decision..."
                    />

                    {triageError && <p className="mi-triage-form__error">{triageError}</p>}

                    <div className="mi-triage-form__buttons">
                      <button
                        type="button"
                        className="mi-triage-form__apply"
                        onClick={() => submitTriage(issue.id)}
                        disabled={triageSubmitting}
                      >
                        {triageSubmitting ? 'Applying…' : 'Apply Triage'}
                      </button>
                      <button type="button" className="mi-triage-form__cancel" onClick={cancelTriage} disabled={triageSubmitting}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="machine-issues ticket-ui bn-page-padding">
      {renderNeedsTriageBanner()}

      {error && <div className="error-message">{error}</div>}

      {view === 'by_machine' && renderByMachineTab()}
      {view === 'needs_triage' && renderNeedsTriageTab()}

      <MachineBottomNav />
    </div>
  );
}

export default MachineIssues;
